import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

/**
 * SalaryComputationInfo — the single place that explains how wage/salary is computed.
 * Renders a small info (ⓘ) button that opens a modal. Reuse this anywhere the topic
 * comes up rather than duplicating the explanation.
 */
export default function SalaryComputationInfo({ className = '' }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="How is salary computed?"
        className={`inline-flex items-center justify-center text-primary-600 hover:text-primary-700 ${className}`}
      >
        <Info size={15} />
      </button>

      {open && (
        <div className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2"><Info size={16} className="text-primary-600" /> How salary &amp; wages are computed</h3>
              <button onClick={() => setOpen(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>

            <div className="p-4 space-y-4 text-sm text-gray-700">
              <p className="text-gray-500">Both pay types use a standard <b>30-day basis</b>: <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">Daily rate = Monthly ÷ 30</span></p>

              <div>
                <p className="font-semibold text-blue-700 mb-1">Salaried (fixed monthly)</p>
                <p>Pay accrues progressively each day of the cycle and is <b>capped at the monthly salary</b>:</p>
                <p className="font-mono text-xs bg-gray-100 rounded px-2 py-1.5 my-1.5">Payable = min( Monthly , Daily rate × payable days )</p>
                <p>Completing the cycle always pays the <b>full month</b> — so the 30th <i>and</i> 31st both complete it, and finishing February (28th/29th) also pays the full salary.</p>
              </div>

              <div>
                <p className="font-semibold text-amber-700 mb-1">Daily wages</p>
                <p>Starts at 0 and accrues <span className="font-mono bg-gray-100 px-1 rounded">Daily rate × days present</span> (a day counts when there's an in + out punch, or paid leave).</p>
              </div>

              <div>
                <p className="font-semibold text-gray-700 mb-1">Example — 45,000 monthly (daily rate = 1,500)</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border rounded overflow-hidden">
                    <thead className="bg-gray-50 text-gray-500">
                      <tr><th className="px-3 py-1.5 text-left">Day reached</th><th className="px-3 py-1.5 text-left">Calculation</th><th className="px-3 py-1.5 text-right">Salary</th></tr>
                    </thead>
                    <tbody className="divide-y">
                      <tr><td className="px-3 py-1.5">29th</td><td className="px-3 py-1.5 font-mono">29 × 1,500</td><td className="px-3 py-1.5 text-right font-semibold">43,500</td></tr>
                      <tr><td className="px-3 py-1.5">30th</td><td className="px-3 py-1.5 font-mono">30 × 1,500</td><td className="px-3 py-1.5 text-right font-semibold">45,000</td></tr>
                      <tr><td className="px-3 py-1.5">31st</td><td className="px-3 py-1.5">capped at monthly</td><td className="px-3 py-1.5 text-right font-semibold">45,000</td></tr>
                      <tr><td className="px-3 py-1.5">28/29 Feb (last day)</td><td className="px-3 py-1.5">completes the month</td><td className="px-3 py-1.5 text-right font-semibold">45,000</td></tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="rounded-lg bg-gray-50 border p-3">
                <p className="font-semibold text-gray-700 mb-1">Running balance</p>
                <p className="font-mono text-xs">Balance = Earned − advances − deductions − salary paid</p>
                <p className="text-xs text-gray-500 mt-1">Positive = still owed to staff · Negative = overpaid (carries forward as credit). Any unpaid balance rolls forward across months.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
